import { motion } from "framer-motion";
import { CheckCircle, Database, Layers, TrendingUp, Zap } from "lucide-react";

export default function ServicesSection() { // Named the component for better debuggability
    const services = [
        { 
            icon: <Zap className="w-10 h-10 text-emerald-400 mb-4" />, 
            title: 'Executive Leadership & Strategy', 
            items: ['CTO Advisory & Mentorship', 'Engineering Management', 'Agile/DevOps Transformation', 'IT Strategy & Governance'] 
        },
        {
            icon: <Layers className="w-10 h-10 text-cyan-400 mb-4" />, 
            title: 'Cloud Architecture & Systems', 
            items: [
                'Cloud-Native Microservices',
                'Event-Driven Systems & Kafka',
                'High-Performance Computing',
                'API-First Development',
                'Blockchain R&D'
            ]
        },
        {
            icon: <TrendingUp className="w-10 h-10 text-indigo-400 mb-4" />, 
            title: 'DevOps, SecOps & Automation', 
            items: [
                'CI/CD Pipeline Implementation (GitOps)',
                'Infrastructure as Code (IaC) - Terraform/CloudFormation',
                'DevSecOps & Security Automation',
                'Multi-Environment Deployment Strategy',
                'Containerization (Docker/Kubernetes)'
            ]
        },
        {
            icon: <Database className="w-10 h-10 text-yellow-400 mb-4" />, 
            title: 'Data Engineering & ETL Services', 
            items: [
                'Data Pipeline Design & Implementation',
                'ETL/ELT Workflow Development',
                'Real-Time Data Processing',
                'Fintech Platform Analytics',
                'Data Warehouse Solutions'
            ]
        }
    ];

    return (
        <section id="services" className="py-20 sm:py-28 bg-gray-950"> {/* Darker background for contrast */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center">
                    <span className="text-sm font-semibold uppercase text-emerald-400 tracking-widest">Core Competencies</span> {/* New accent color */}
                    <h2 className="mt-2 text-4xl font-extrabold tracking-tight text-white sm:text-5xl"> {/* Bolder heading */}
                        What I Deliver
                    </h2>
                    <p className="mt-4 text-xl text-gray-400 max-w-3xl mx-auto font-light">
                        I combine strategic vision with hands-on architecture to solve your most complex technology and leadership challenges, driving measurable business results.
                    </p>
                </div>

                <div className="mt-20 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
                    {services.map((card, index) => (
                        <motion.div
                            key={card.title}
                            className="bg-gray-900 p-8 rounded-2xl shadow-2xl border border-gray-800 text-gray-300 transition duration-300"
                            initial={{ opacity: 0, y: 30, scale: 0.95 }}
                            whileInView={{ opacity: 1, y: 0, scale: 1 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.6, delay: index * 0.15, ease: "easeOut" }}
                            whileHover={{ 
                                y: -8, 
                                borderColor: '#06B6D4', // cyan-500
                                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                                transition: { duration: 0.3 }
                            }}
                        >
                            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-800 mb-6">
                                {card.icon}
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-5 border-b border-gray-700/50 pb-3">{card.title}</h3>
                            <ul className="space-y-3">
                                {card.items.map(item => (
                                    <li key={item} className="flex items-start text-sm">
                                        <CheckCircle className="w-4 h-4 text-cyan-400 mr-2 mt-1 flex-shrink-0" />
                                        <span className="font-medium">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    )
}